import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Clock, Users, Flame, Beef, Wheat, Droplets, Activity, Lock, Sparkles, ChefHat, Eye } from "lucide-react";
import { useLang } from "@/lib/LanguageContext";
import { useAuth } from "@/lib/AuthContext";
import axios from "axios";

const SHARED_API = process.env.REACT_APP_API_URL;

export default function SharedRecipe() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [viewCount, setViewCount] = useState(null);
  const [signupCount, setSignupCount] = useState(null);

  let recipe = null;
  try {
    const data = params.get("d");
    if (data) {
      // UTF-8 safe base64 decoding
      recipe = JSON.parse(decodeURIComponent(escape(atob(data))));
    }
  } catch {
    // invalid data
  }

  // Generate a hash from recipe title for view tracking
  const recipeHash = recipe ? btoa(unescape(encodeURIComponent(recipe.title))).slice(0, 40) : null;

  useEffect(() => {
    if (!recipeHash) return;
    axios.post(`${SHARED_API}/api/shared/view`, { recipe_hash: recipeHash })
      .then((r) => {
        setViewCount(r.data.views);
        setSignupCount(r.data.signups || 0);
      })
      .catch(() => {
        axios.get(`${SHARED_API}/api/shared/views/${recipeHash}`)
          .then((r) => {
            setViewCount(r.data.views);
            setSignupCount(r.data.signups || 0);
          })
          .catch(() => {});
      });
  }, [recipeHash]);

  const handleRegisterClick = () => {
    if (recipeHash) {
      localStorage.setItem("shared_recipe_hash", recipeHash);
    }
    navigate("/register");
  };

  const handleLoginClick = () => {
    if (recipeHash) {
      localStorage.setItem("shared_recipe_hash", recipeHash);
    }
    navigate("/login");
  };

  if (!recipe) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4" data-testid="shared-page-empty">
        <ChefHat className="w-16 h-16 text-stone-700 mb-4" />
        <h1 className="font-heading text-2xl text-white mb-2">Recette introuvable</h1>
        <p className="text-stone-500 mb-6">{"Le lien de partage est invalide ou a expir\u00e9."}</p>
        <Link
          to="/"
          className="px-6 py-3 bg-amber-500 text-stone-950 font-semibold rounded-full hover:bg-amber-600 transition-colors"
        >
          Découvrir What To Cook
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="shared-recipe-page">
      {/* Hero Image */}
      <div className="relative h-[35vh] sm:h-[45vh] overflow-hidden bg-stone-900">
        {recipe.img ? (
          <img
            src={`data:image/jpeg;base64,${recipe.img}`}
            alt={recipe.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ChefHat className="w-20 h-20 text-stone-700" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0C0A09] via-[#0C0A09]/40 to-transparent" />
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 -mt-16 relative z-10 pb-20">
        {/* Title */}
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white mb-3">
          {recipe.title}
        </h1>

        {/* View Counter */}
        {viewCount != null && viewCount > 0 && (
          <div className="flex items-center gap-2 mb-4" data-testid="view-counter">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-full">
              <Eye className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-sm font-medium text-amber-500">{viewCount}</span>
              <span className="text-xs text-amber-500/70">
                {viewCount === 1 ? "personne a vu cette recette" : "personnes ont d\u00e9j\u00e0 vu cette recette"}
              </span>
            </div>
          </div>
        )}

        {recipe.desc && (
          <p className="text-stone-400 leading-relaxed mb-6">{recipe.desc}</p>
        )}

        {/* Time/Servings */}
        <div className="flex flex-wrap gap-4 mb-6">
          {recipe.prep && (
            <div className="flex items-center gap-2 px-4 py-2 bg-[#1C1917] border border-white/5 rounded-xl">
              <Clock className="w-4 h-4 text-amber-500" />
              <div>
                <p className="text-[10px] uppercase text-stone-500 tracking-wider">{t("prepTime")}</p>
                <p className="text-sm text-white">{recipe.prep}</p>
              </div>
            </div>
          )}
          {recipe.cook && (
            <div className="flex items-center gap-2 px-4 py-2 bg-[#1C1917] border border-white/5 rounded-xl">
              <Clock className="w-4 h-4 text-orange-500" />
              <div>
                <p className="text-[10px] uppercase text-stone-500 tracking-wider">{t("cookTime")}</p>
                <p className="text-sm text-white">{recipe.cook}</p>
              </div>
            </div>
          )}
          {recipe.serv && (
            <div className="flex items-center gap-2 px-4 py-2 bg-[#1C1917] border border-white/5 rounded-xl">
              <Users className="w-4 h-4 text-green-500" />
              <div>
                <p className="text-[10px] uppercase text-stone-500 tracking-wider">{t("servings")}</p>
                <p className="text-sm text-white">{recipe.serv}</p>
              </div>
            </div>
          )}
        </div>

        {/* Nutrition - VISIBLE */}
        {recipe.nut && (
          <div className="mb-8" data-testid="shared-nutrition">
            <h2 className="font-heading text-xl font-medium text-white mb-4">{t("nutritionInfo")}</h2>
            <div className="bg-[#1C1917] border border-white/5 rounded-2xl p-5">
              {recipe.hs != null && (
                <div className="flex items-center gap-3 mb-4 pb-4 border-b border-white/5">
                  <div className="relative w-12 h-12 flex-shrink-0">
                    <svg className="w-12 h-12 -rotate-90" viewBox="0 0 48 48">
                      <circle cx="24" cy="24" r="20" fill="none" stroke="#292524" strokeWidth="3.5" />
                      <circle cx="24" cy="24" r="20" fill="none"
                        stroke={recipe.hs >= 7 ? "#22c55e" : recipe.hs >= 4 ? "#f59e0b" : "#ef4444"}
                        strokeWidth="3.5" strokeLinecap="round"
                        strokeDasharray={`${(recipe.hs / 10) * 125.6} 125.6`}
                      />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-white">{recipe.hs}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{"Score sant\u00e9"}</p>
                    <p className="text-xs text-stone-500">sur 10</p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <NutriDisplay icon={<Flame className="w-4 h-4" />} label={t("calories")} value={recipe.nut.cal} unit="kcal" color="text-orange-500" bg="bg-orange-500/10" />
                <NutriDisplay icon={<Beef className="w-4 h-4" />} label={t("proteins")} value={recipe.nut.prot} unit="g" color="text-red-400" bg="bg-red-500/10" />
                <NutriDisplay icon={<Wheat className="w-4 h-4" />} label={t("carbs")} value={recipe.nut.carb} unit="g" color="text-amber-500" bg="bg-amber-500/10" />
                <NutriDisplay icon={<Droplets className="w-4 h-4" />} label={t("fats")} value={recipe.nut.fat} unit="g" color="text-yellow-500" bg="bg-yellow-500/10" />
              </div>
            </div>
          </div>
        )}

        {/* Ingredients - BLURRED */}
        <div className="mb-8 relative" data-testid="shared-ingredients-blurred">
          <h2 className="font-heading text-xl font-medium text-white mb-4">{t("ingredients")}</h2>
          <div className="bg-[#1C1917] border border-white/5 rounded-2xl p-5 relative overflow-hidden">
            <div className="blur-[6px] select-none pointer-events-none">
              <ul className="space-y-2.5">
                {["200g de poulet marine aux epices douces", "3 tomates bien mures coupees en des", "2 oignons eminces finement", "1 cuillere a soupe d'huile d'olive", "Sel, poivre, herbes de Provence", "150ml de bouillon de legumes"].map((fake, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-stone-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                    {fake}
                  </li>
                ))}
              </ul>
            </div>
            <div className="absolute inset-0 flex items-center justify-center bg-[#1C1917]/60 backdrop-blur-[2px]">
              <div className="text-center">
                <Lock className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="text-sm text-white font-medium">{"Contenu r\u00e9serv\u00e9 aux membres"}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Instructions - BLURRED */}
        <div className="mb-10 relative" data-testid="shared-instructions-blurred">
          <h2 className="font-heading text-xl font-medium text-white mb-4">{t("instructions")}</h2>
          <div className="space-y-3 relative overflow-hidden">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex gap-4 bg-[#1C1917] border border-white/5 rounded-2xl p-5 blur-[6px] select-none pointer-events-none">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <span className="text-sm font-semibold text-amber-500">{i}</span>
                </div>
                <p className="text-sm text-stone-300 leading-relaxed pt-1">
                  Lorem ipsum dolor sit amet consectetur adipisicing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                </p>
              </div>
            ))}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center bg-[#0C0A09]/70 backdrop-blur-[2px] rounded-2xl px-8 py-6">
                <Lock className="w-8 h-8 text-amber-500 mx-auto mb-3" />
                <p className="text-white font-medium mb-1">{"Envie de voir la recette compl\u00e8te ?"}</p>
                <p className="text-xs text-stone-500 mb-4">{"Inscrivez-vous gratuitement sur What To Cook"}</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10 border border-amber-500/20 rounded-2xl p-8 text-center" data-testid="shared-cta">
          <img
            src="https://customer-assets.emergentagent.com/job_flavor-forge-68/artifacts/rz0qyx4s_Firefly_GeminiFlash%202.png"
            alt="What To Cook"
            className="w-14 h-14 rounded-xl mx-auto mb-4"
          />
          <h3 className="font-heading text-xl font-medium text-white mb-2">
            {"Inscrivez-vous pour voir la recette compl\u00e8te"}
          </h3>
          <p className="text-sm text-stone-400 mb-4 max-w-md mx-auto">
            {"G\u00e9n\u00e9rez des recettes personnalis\u00e9es par IA \u00e0 partir de vos ingr\u00e9dients ou du nom de plats connus"}
          </p>
          {/* Signup social proof */}
          {signupCount != null && signupCount > 0 && (
            <div className="mb-5" data-testid="signup-counter">
              <p className="text-sm text-green-400 font-medium">
                {signupCount} {signupCount === 1 ? "personne s'est inscrite" : "personnes se sont inscrites"} {"gr\u00e2ce \u00e0 cette recette"}
              </p>
            </div>
          )}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={handleRegisterClick}
              data-testid="shared-register-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-stone-950 font-semibold rounded-full hover:bg-amber-600 transition-all hover:shadow-lg hover:shadow-amber-500/20"
            >
              <Sparkles className="w-5 h-5" />
              {"Cr\u00e9er un compte gratuit"}
            </button>
            {!user && (
              <button
                onClick={handleLoginClick}
                data-testid="shared-login-btn"
                className="px-6 py-3 text-sm text-stone-400 hover:text-white transition-colors"
              >
                {"D\u00e9j\u00e0 membre ? Se connecter"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function NutriDisplay({ icon, label, value, unit, color, bg }) {
  if (value == null) return null;
  return (
    <div className="flex flex-col items-center gap-2 p-3 bg-stone-900/50 rounded-xl">
      <div className={`w-8 h-8 rounded-full ${bg} flex items-center justify-center ${color}`}>{icon}</div>
      <div className="text-center">
        <p className="text-lg font-semibold text-white">{value}</p>
        <p className="text-[10px] text-stone-500 uppercase tracking-wider">{unit}</p>
      </div>
      <p className="text-xs text-stone-400">{label}</p>
    </div>
  );
}
